import request from './index';
import { BACKEND_ERROR_MESSAGE } from '../utils/constants/content-production';
import IMS from './ims';

const BASE_API_URL = process.env.LCS_API_ENDPOINT ? process.env.LCS_API_ENDPOINT : 'http://localhost:8080/api/';
const BASE_INAPP_API_URL = process.env.INAPP_API_ENDPOINT
    ? process.env.INAPP_API_ENDPOINT
    : 'http://localhost:8080/api/';
const BASE_UTUT_API_URL = process.env.UTUT_API_ENDPOINT ? process.env.UTUT_API_ENDPOINT : 'http://localhost:8080/api/';
const BASE_PLAYLIST_API_URL = process.env.PLAYLIST_API_ENDPOINT
    ? process.env.PLAYLIST_API_ENDPOINT
    : 'http://localhost:8080/api/';

const API_KEY = 'LearnContentPortal1';
class LCSServices {
    getRequestHeaders = () => ({
        'x-api-key': API_KEY,
        'Content-Type': 'application/json',
        Accept: 'application/json',
        authorization: '',
    });

    getBaseHeaders = () => {
        const token = IMS.getInstance().getAccessToken();
        return {
            ...this.getRequestHeaders(),
            authorization: `Bearer ${token}`,
        };
    };

    /**
     * Fetch user entity from Learn Content Service using entity management API
     * @param domainId - Domain ID (e.g., 'lcp')
     * @param entityType - Entity type (e.g., 'user')
     * @returns User entity with roles
     */
    fetchUserEntityFromLCS = async (domainId: string = 'lcp', entityType: string = 'user') => {
        try {
            const headers = this.getBaseHeaders();
            const url = `${BASE_API_URL}/v1/domains/${domainId}/entity-types/${entityType}/entity-ids/me`;
            const response = await request(url, headers, BACKEND_ERROR_MESSAGE, {});
            return response;
        } catch (error: unknown) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            console.error(`${BACKEND_ERROR_MESSAGE} LCS > fetch user entity > ${errorMessage}`);
            return { data: error, failed: true };
        }
    };

    /**
     * Fetch user entity from InApp Authoring Service using entity management API
     * @param domainId - Domain ID (e.g., 'lcp')
     * @param entityType - Entity type (e.g., 'user')
     * @returns User entity with roles
     */
    fetchUserEntityFromInApp = async (domainId: string = 'tbw', entityType: string = 'user') => {
        try {
            const headers = this.getBaseHeaders();
            const url = `${BASE_INAPP_API_URL}/v1/domains/${domainId}/entity-types/${entityType}/entity-ids/me`;
            const response = await request(url, headers, BACKEND_ERROR_MESSAGE, {});
            return response;
        } catch (error: unknown) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            console.error(`${BACKEND_ERROR_MESSAGE} InappAuthoring > fetch user entity > ${errorMessage}`);
            return { data: error, failed: true };
        }
    };

    /**
     * @see https://jira.corp.adobe.com/browse/LCP-1604
     *
     * @param urls String array of urls to validate
     * @param appName Optional appName
     */
    validateUrls = async (urls: string[], appName?: string) => {
        const params = appName ? `?appName=${appName}` : '';
        const url = `${BASE_INAPP_API_URL}/validation/urls${params}`;

        const headers = this.getBaseHeaders();
        const response = await request(url, headers, BACKEND_ERROR_MESSAGE, {}, 'POST', { urls });

        // handle validation requests that fail or timeout e.g. due to non-public links
        // this is expected as per the https://jira.corp.adobe.com/browse/LCP-1604 comments
        if (!response || (response.failed && !response.data?.data?.details)) {
            return { success: false, reason: 'failed' };
        }
        return response?.failed ? { success: false, reason: response.data.data.details.reason } : { success: true };
    };

    formateUtutDataUrl = (
        surface?: string,
        locale?: string,
        app?: string,
        ututType?: string,
        sortBy?: string,
        sortOrder?: string,
        aem_id?: string,
        apiKey?: string,
    ) => {
        let params = locale ? `locale=${locale}` : '';

        // This should only be provided when generating a url for a preview link. Otherwise, provide the apiKey in req headers
        if (apiKey) {
            params = `${params}&api_key=${apiKey}`;
        }

        if (ututType) {
            params = `${params}&status=${ututType}`;
        }

        if (aem_id) {
            params = `${params}&aem_id=${aem_id}`;
        } else {
            switch (app) {
                case 'ILST':
                    params = `${params}&metadata.application.sap_code=ILST`;
                    break;
                case 'PHSP':
                    params = `${params}&metadata.application.sap_code=PHSP`;
                    break;
                case 'IDSN':
                    params = `${params}&metadata.application.sap_code=IDSN`;
                    break;
                case 'all':
                    params = `${params}&metadata.application.sap_code=PHSP&metadata.application.sap_code=ILST&metadata.application.sap_code=IDSN`;
                    break;
            }
            if (surface) {
                params = `${params}&inline_surface=${surface}`;
            }
            if (sortBy) {
                params = `${params}&sort=${sortBy}`;
            }
            if (sortOrder) {
                params = `${params}&sort_order=${sortOrder}`;
            }
            params = `${params}&per_page=10000&page=1`;
        }
        return params;
    };

    getUtutUrl = (locale?: string, status?: string, aem_id?: string, surface?: string) => {
        try {
            const params = this.formateUtutDataUrl(surface, locale, '', status, '', '', aem_id, 'LearnContentService1');
            const url = `${BASE_UTUT_API_URL}/ututs?${params}`;
            return url;
        } catch (error: unknown) {
            // TODO: ADD ERROR LOGGING
            console.error('error ====>', error);
            return { data: error, failed: true };
        }
    };

    getPlaylistUrl = (locale?: string, status?: string, aem_id?: string, surface?: string) => {
        try {
            const params = this.formateUtutDataUrl(surface, locale, '', status, '', '', aem_id, 'LearnContentService1');
            const url = `${BASE_PLAYLIST_API_URL}/playlists?${params}`;
            return url;
        } catch (error: unknown) {
            // TODO: ADD ERROR LOGGING
            console.error('error ====>', error);
            return { data: error, failed: true };
        }
    };

    getCollectionUrl = (locale?: string, status?: string, aem_id?: string, surface?: string) => {
        try {
            const params = this.formateUtutDataUrl(surface, locale, '', status, '', '', aem_id, 'LearnContentService1');
            const url = `${BASE_PLAYLIST_API_URL}/collections?${params}`;
            return url;
        } catch (error: unknown) {
            // TODO: ADD ERROR LOGGING
            console.error('error ====>', error);
            return { data: error, failed: true };
        }
    };

    fetchUtutDashBoardData = async (
        surface?: string,
        locale?: string,
        app?: string,
        ututType?: string,
        sortBy?: string,
        sortOrder?: string,
        aem_id?: string,
    ) => {
        try {
            const params = this.formateUtutDataUrl(surface, locale, app, ututType, sortBy, sortOrder, aem_id);
            const url = `${BASE_API_URL}/ututdata?${params}`;
            const headers = this.getBaseHeaders();
            const response = await request(url, headers, BACKEND_ERROR_MESSAGE, {});
            return response;
        } catch (error: unknown) {
            // TODO: ADD ERROR LOGGING
            console.error('error ====>', error);
            return { data: error, failed: true };
        }
    };

    fetchUtut = async (locale?: string, ututType?: string, aem_id?: string) => {
        try {
            const params = this.formateUtutDataUrl('', locale, '', ututType, '', '', aem_id);
            const url = `${BASE_API_URL}/ututs?${params}`;
            const headers = this.getBaseHeaders();
            const response = await request(url, headers, BACKEND_ERROR_MESSAGE, {});
            if (!response?.failed) {
                return response?.data?.tutorials;
            }
            return response;
        } catch (error: unknown) {
            // TODO: ADD ERROR LOGGING
            console.error('error ====>', error);
            return { data: error, failed: true };
        }
    };

    fetchPL = async (locale?: string, status?: string, aem_id?: string) => {
        try {
            const params = this.formatPlayListDataUrl('', locale, status, '', '', aem_id);
            const url = `${BASE_API_URL}/playlists?${params}`;
            const headers = this.getBaseHeaders();
            const response = await request(url, headers, BACKEND_ERROR_MESSAGE, {});
            if (!response?.failed) {
                return response?.data;
            }
            return response;
        } catch (error: unknown) {
            // TODO: ADD ERROR LOGGING
            console.error('error ====>', error);
            return { data: error, failed: true };
        }
    };

    _cleanPlaylistData = (data) => {
        const cleanedPlaylist = JSON.parse(JSON.stringify(data));
        if (cleanedPlaylist.playlist_stage && cleanedPlaylist.playlist_stage === 'None')
            delete cleanedPlaylist.playlist_stage;

        // clean the sections from the metadata that is only mapped for the frontend tables
        if (cleanedPlaylist.sections && Array.isArray(cleanedPlaylist.sections)) {
            cleanedPlaylist.sections.forEach((section: { items?: { type?: string; _metadata?: unknown }[] }) => {
                if (section.items && Array.isArray(section.items)) {
                    section.items.forEach((sectionItem: { type?: string; _metadata?: unknown }) => {
                        sectionItem['type'] = 'tutorial';
                        delete sectionItem['_metadata'];
                    });
                }
            });
        }
        return cleanedPlaylist;
    };

    upsertPL = async (data) => {
        try {
            data = this._cleanPlaylistData(data);
            const url = `${BASE_API_URL}/playlist`;
            console.log('final url ===>', url);
            const headers = this.getBaseHeaders();
            const response = await request(url, headers, BACKEND_ERROR_MESSAGE, {}, 'PUT', data);
            if (!response?.failed) {
                return response?.data;
            }
            return response;
        } catch (error: unknown) {
            // TODO: ADD ERROR LOGGING
            console.error('error ====>', error);
            return { data: error, failed: true };
        }
    };

    _cleanCollectionData = (collection) => {
        const cleanedCollection = JSON.parse(JSON.stringify(collection));
        if (cleanedCollection.collection_stage && cleanedCollection.collection_stage === 'None')
            delete cleanedCollection.collection_stage;

        // clean the sections from the metadata that is only mapped for the frontend tables
        if (cleanedCollection.sections && Array.isArray(cleanedCollection.sections)) {
            cleanedCollection.sections.forEach((section: { items?: { type?: string; _metadata?: unknown }[] }) => {
                if (section.items && Array.isArray(section.items)) {
                    section.items.forEach((sectionItem: { type?: string; _metadata?: unknown }) => {
                        sectionItem['type'] = 'playlist';
                        delete sectionItem['_metadata'];
                    });
                }
            });
        }
        return cleanedCollection;
    };

    upsertCL = async (data) => {
        try {
            data = this._cleanCollectionData(data);
            const url = `${BASE_API_URL}/collection`;
            console.log('final url ===>', url);
            const headers = this.getBaseHeaders();
            const response = await request(url, headers, BACKEND_ERROR_MESSAGE, {}, 'PUT', data);
            if (!response?.failed) {
                return response?.data;
            }
            return response;
        } catch (error: unknown) {
            // TODO: ADD ERROR LOGGING
            console.error('error ====>', error);
            return { data: error, failed: true };
        }
    };

    getTopicsDromGroup = (taxonomy) => {
        const rel: string[] = [];
        taxonomy.forEach((element) => {
            if (element?.name) {
                rel.push(element?.['name']);
            }
        });
        return rel;
    };
    extractMetaDataFromUtut = (utut: object) => {
        const metaData = utut?.['metadata'];
        const obj: {
            side1?: Record<string, unknown>;
            side2?: Record<string, unknown>;
            side3?: Record<string, unknown>;
            [key: string]: unknown;
        } = {};
        obj['aem_id'] = utut?.['aem_id'] ?? '';
        if (Object.keys(metaData).length !== 0) {
            const side1 = {},
                side2 = {},
                side3 = {};
            side1['image'] = metaData?.images?.hero ?? '';
            side1['title'] = metaData?.title ?? '';
            side1['description'] = metaData?.descriptions?.long ?? '';

            side2['created_on'] = utut?.['created_on'] ?? '';
            side2['modified_on'] = utut?.['modified_on'] ?? '';
            side2['topics'] = metaData['taxonomy']
                ? this.getTopicsDromGroup(metaData['taxonomy']?.['topics'] ?? [])
                : (metaData?.topics ?? []);
            side2['stage'] = metaData?.['stage'] ?? '';
            side2['level'] = metaData?.['level'] ?? '';
            side2['media_types'] = metaData?.['media_types'] ?? '';

            side3['creative_fields'] = metaData?.['creative_fields'] ?? '';
            side3['learning_approach'] = metaData?.['learning_approach'] ?? '';
            side3['duration_in_seconds'] = metaData['duration_in_seconds'] ?? 120;
            side3['fi_code'] = metaData?.['application']?.fi_code ?? '';
            side3['sap_code'] = metaData?.['application']?.sap_code ?? '';
            side3['rel_fi_code'] = metaData?.['related_applications']?.fi_code ?? '';
            side3['rel_sap_code'] = metaData?.['related_applications']?.sap_code ?? '';

            obj.side1 = side1;
            obj.side2 = side2;
            obj.side3 = side3;
        }
        return obj;
    };

    dashBoardCardData = async (locale?: string, ututType?: string, aem_id?: string) => {
        try {
            const ututs = await this.fetchUtut(locale, ututType, aem_id);
            const utut = ututs.length ? ututs[0] : {};

            const data = this.extractMetaDataFromUtut(utut);
            return data;
        } catch (error: unknown) {
            // TODO: ADD ERROR LOGGING
            console.error('error ====>', error);
            return { data: error, failed: true };
        }
    };

    extractMetaDataFromPL = (playlist: object, locale: string) => {
        const obj: { side1?: Record<string, unknown>; side2?: Record<string, unknown> } = {};
        const side1 = {},
            side2 = {};
        side1['title'] = playlist?.['title'] ?? '';
        side1['heroImage'] = playlist?.['images']?.['hero'] ?? '';
        side1['description'] = playlist?.['description'] ?? '';
        side1['formatVersion'] = playlist?.['formatVersion'] ?? '1.0';
        side1['primaryProduct'] = playlist?.['applications']?.['sap_code'][0];
        side1['aemId'] = playlist?.['aem_id'] ?? '';
        side2['learnStage'] = playlist?.['stage'] ?? '';
        side2['playlistStage'] = playlist?.['playlist_stage'] ?? '';
        side2['creativeFields'] = playlist?.['creative_fields'] ?? [];
        side2['creativeCategory'] = playlist?.['creative_categories']?.[0] ?? '';
        side2['status'] = playlist?.['status'] ?? [];
        side2['level'] = playlist?.['level'] ?? '';
        side2['loc'] = locale;
        side2['created_on'] = playlist?.['created_on'] ?? '';
        obj.side1 = side1;
        obj.side2 = side2;
        return obj;
    };

    dashBoardCardDataPL = async (locale: string, ututType?: string, aem_id?: string, forCard?: boolean) => {
        try {
            const playLists = await this.fetchPL(locale, ututType, aem_id);
            const pl = playLists.length ? playLists[0] : {};
            const data = forCard ? this.extractMetaDataFromPL(pl, locale) : pl;
            return data;
        } catch (error: unknown) {
            // TODO: ADD ERROR LOGGING
            console.error('error ====>', error);
            return { data: error, failed: true };
        }
    };

    formateCollectionDataUrl = (
        surface?: string,
        locale?: string,
        status?: string,
        sortBy?: string,
        sortOrder?: string,
        aem_id?: string,
    ) => {
        let params = locale ? `locale=${locale}` : '';
        switch (status) {
            case 'draft':
                params = `${params}&status=draft`;
                break;
            case 'live':
                params = `${params}&status=live`;
                break;
            case 'all':
                params = `${params}&status=draft&status=live`;
                break;
            case '':
            default:
                params = `${params}&status=draft`;
        }

        if (aem_id) {
            params = `${params}&aem_id=${aem_id}`;
        } else {
            switch (surface) {
                case 'helpx':
                    params = `${params}&surface_type=helpx`;
                    break;
                case 'in-app':
                    params = `${params}&surface_type=in-app`;
                    break;
                case 'education':
                    params = `${params}&surface_type=education`;
                    break;
                case 'all':
                    params = `${params}&surface_type=in-app&surface_type=helpx&surface_type=education`;
                    break;
            }
            if (sortBy) {
                params = `${params}&sort=${sortBy}`;
            }
            if (sortOrder) {
                params = `${params}&sort_order=${sortOrder}`;
            }
        }
        // params = `${params}&mock=true`;
        return params;
    };

    fetchCL = async (locale?: string, status?: string, aem_id?: string) => {
        try {
            const params = this.formateCollectionDataUrl('', locale, status, '', '', aem_id);
            const url = `${BASE_API_URL}/collections?${params}`;
            const headers = this.getBaseHeaders();
            const response = await request(url, headers, BACKEND_ERROR_MESSAGE, {});
            if (!response?.failed) {
                return response?.data;
            }
            return response;
        } catch (error: unknown) {
            // TODO: ADD ERROR LOGGING
            console.error('error ====>', error);
            return { data: error, failed: true };
        }
    };

    extractMetaDataFromCL = (collectionData: object, _locale: string) => {
        const obj: { side1?: Record<string, unknown>; side2?: Record<string, unknown> } = {};
        const side1 = {},
            side2 = {};
        side1['title'] = collectionData?.['title'] ?? '';
        side1['heroImage'] = collectionData?.['images']?.['hero'] ?? '';
        side1['description'] = collectionData?.['description'] ?? '';
        side1['goal'] = collectionData?.['goal'] ?? '';
        side2['primaryProduct'] = collectionData?.['applications']?.['sap_code'][0];
        side2['totalDuration'] = collectionData?.['total_duration'] ?? '';
        side2['level'] = collectionData?.['level'] ?? '';
        side2['collectionStage'] = collectionData?.['collection_stage'] ?? '';
        side2['Surface'] = collectionData?.['platforms']
            ? collectionData?.['platforms'].length > 0
                ? collectionData?.['platforms'][0]
                : ''
            : '';
        side2['useCase'] = collectionData?.['useCase'] ?? [];
        side2['created_on'] = collectionData?.['created_on'] ?? '';
        obj.side1 = side1;
        obj.side2 = side2;
        return obj;
    };

    dashBoardCardDataCL = async (locale: string, ututType?: string, aem_id?: string, forCard?: boolean) => {
        try {
            const collections = await this.fetchCL(locale, ututType, aem_id);
            const cl = collections.length ? collections[0] : {};
            const data = forCard ? this.extractMetaDataFromCL(cl, locale) : cl;
            return data;
        } catch (error: unknown) {
            // TODO: ADD ERROR LOGGING
            console.error('error ====>', error);
            return { data: error, failed: true };
        }
    };

    duplicatePlaylistUtutdata = async (locale: string, status?: string, aem_id?: string, targetLocale?: string) => {
        try {
            const url = `${BASE_API_URL}/playlist/ututdata?aem_id=${aem_id}&locale=${locale}&status=${status}&targetlocale=${targetLocale}`;
            console.log('final url ===>', url);
            const headers = this.getBaseHeaders();
            const response = await request(url, headers, BACKEND_ERROR_MESSAGE, {}, 'GET', {});
            return response;
        } catch (error: unknown) {
            // TODO: ADD ERROR LOGGING
            console.error('error ====>', error);
            return { data: error, failed: true };
        }
    };

    duplicateCollectionPLdata = async (locale: string, status?: string, aem_id?: string, targetLocale?: string) => {
        try {
            const url = `${BASE_API_URL}/collection/playlistdata?aem_id=${aem_id}&locale=${locale}&status=${status}&targetlocale=${targetLocale}`;
            console.log('final url ===>', url);
            const headers = this.getBaseHeaders();
            const response = await request(url, headers, BACKEND_ERROR_MESSAGE, {}, 'GET', {});
            return response;
        } catch (error: unknown) {
            // TODO: ADD ERROR LOGGING
            console.error('error ====>', error);
            return { data: error, failed: true };
        }
    };

    publishToLiveUtuts = async (SelectedUtuts) => {
        const res: { data: { ututs: Array<Record<string, unknown>> } } = { data: { ututs: [] } };
        await Promise.all(
            SelectedUtuts.map((utut) => {
                const { aem_id, surface, locale, status } = utut;
                const obj: Record<string, unknown> = {};
                obj['aemId'] = aem_id;
                obj['surface'] = surface === 'in_app_panel' ? 'in-app' : surface === 'help_json' ? 'helpx' : surface;
                obj['locale'] = locale;
                obj['status'] = status === 'archived' ? 'live' : status;
                res.data.ututs.push(obj);
            }),
        );
        return res;
    };

    archiveUtuts = async (SelectedUtuts) => {
        const res: { data: { ututs: Array<Record<string, unknown>> } } = { data: { ututs: [] } };
        await Promise.all(
            SelectedUtuts.map((utut) => {
                const { aem_id, surface, locale, status } = utut;
                const obj: Record<string, unknown> = {};
                obj['aem_id'] = aem_id;
                obj['surface'] = surface; /*=== "in_app_panel"
            ? "in-app"
            : surface === "help_json"
            ? "helpx"
            : surface;*/
                obj['locale'] = locale;
                obj['status'] = status === 'archived' ? 'live' : status;
                res.data.ututs.push(obj);
            }),
        );
        return res;
    };
    publishToLive = async (SelectedUtuts) => {
        try {
            const url = `${BASE_API_URL}/ututs/publish/live`;
            const data = await this.publishToLiveUtuts(SelectedUtuts);
            const headers = this.getBaseHeaders();
            const response = await request(url, headers, BACKEND_ERROR_MESSAGE, {}, 'POST', data, 15000);
            return response;
        } catch (error: unknown) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            // TODO: ADD ERROR LOGGING
            console.error(`${BACKEND_ERROR_MESSAGE} LCS > publish ututs > ${errorMessage}`);
            return { data: error, failed: true };
        }
    };

    noIndexingUtuts = async (SelectedUtuts, archiveFlag) => {
        try {
            const url = `${BASE_API_URL}/ututs/noindex`;
            const data = await this.archiveUtuts(SelectedUtuts);
            const headers = this.getBaseHeaders();
            const response = await request(
                url,
                headers,
                BACKEND_ERROR_MESSAGE,
                { noindex: archiveFlag ? 'archive' : 'unarchive' },
                'PUT',
                data,
                15000,
            );
            return response;
        } catch (error: unknown) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            // TODO: ADD ERROR LOGGING
            console.error(`${BACKEND_ERROR_MESSAGE} LCS > publish ututs > ${errorMessage}`);
            return { data: error, failed: true };
        }
    };

    sendToLocUtuts = async (SelectedUtuts, targetLocales) => {
        const res: { data: { ututs: Array<Record<string, unknown>> } } = { data: { ututs: [] } };
        await Promise.all(
            SelectedUtuts.map((utut) => {
                const obj: Record<string, unknown> = {};
                obj['aemId'] = utut?.aem_id ?? utut?.aemId;
                obj['sourcelocales'] = ['en_US'];
                obj['targetlocales'] = targetLocales;
                obj['status'] = utut.status;
                res.data.ututs.push(obj);
            }),
        );
        return res;
    };
    sendToLoc = async (SelectedUtuts, targetLocales) => {
        try {
            const url = `${BASE_API_URL}/ututs/sendtolocv2`;
            const data = await this.sendToLocUtuts(SelectedUtuts, targetLocales);
            const headers = this.getBaseHeaders();
            const response = await request(url, headers, BACKEND_ERROR_MESSAGE, {}, 'POST', data, 20000);
            return response;
        } catch (error: unknown) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            // TODO: ADD ERROR LOGGING
            console.error(`${BACKEND_ERROR_MESSAGE} LCS > Send To Loc > ${errorMessage}`);
            return { data: error, failed: true };
        }
    };
    refreshToLoc = async (SelectedUtuts, targetLocales) => {
        try {
            const url = `${BASE_API_URL}/ututs/refreshtoloc`;
            const data = await this.sendToLocUtuts(SelectedUtuts, targetLocales);
            const headers = this.getBaseHeaders();
            const response = await request(url, headers, BACKEND_ERROR_MESSAGE, {}, 'POST', data, 20000);
            return response;
        } catch (error: unknown) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            // TODO: ADD ERROR LOGGING
            console.error(`${BACKEND_ERROR_MESSAGE} LCS > Refresh To Loc > ${errorMessage}`);
            return { data: error, failed: true };
        }
    };

    formatPlayListDataUrl = (
        surface?: string,
        locale?: string,
        status?: string,
        sortBy?: string,
        sortOrder?: string,
        aem_id?: string,
    ) => {
        let params = locale ? `locale=${locale}` : '';
        switch (status) {
            case 'draft':
                params = `${params}&status=draft`;
                break;
            case 'live':
                params = `${params}&status=live`;
                break;
            case 'all':
                params = `${params}&status=draft&status=live`;
                break;
            case '':
            default:
                params = `${params}&status=draft`;
        }

        if (aem_id) {
            params = `${params}&aem_id=${aem_id}`;
        } else {
            switch (surface) {
                case 'helpx':
                    params = `${params}&surface_type=helpx`;
                    break;
                case 'in-app':
                    params = `${params}&surface_type=in-app`;
                    break;
                case 'all':
                    params = `${params}&surface_type=in-app&surface_type=helpx`;
                    break;
            }
            if (sortBy) {
                params = `${params}&sort=${sortBy}`;
            }
            if (sortOrder) {
                params = `${params}&sort_order=${sortOrder}`;
            }
        }
        // params = `${params}&mock=true`;
        return params;
    };

    fetchPlayListDashBoardData = async (
        surface?: string,
        locale?: string,
        status?: string,
        sortBy?: string,
        sortOrder?: string,
        aem_id?: string,
    ) => {
        try {
            const params = this.formatPlayListDataUrl(surface, locale, status, sortBy, sortOrder, aem_id);

            const url = `${BASE_API_URL}/playlistsdata?${params}`;
            const headers = this.getBaseHeaders();
            const response = await request(url, headers, BACKEND_ERROR_MESSAGE, {});
            return response;
        } catch (error: unknown) {
            // TODO: ADD ERROR LOGGING
            console.error('error ====>', error);
            return { data: error, failed: true };
        }
    };

    formatDeletePL = (playlists) => {
        const arr: { aem_id: string; locale: string; status: string }[] = [];
        playlists.forEach((pl) => {
            const obj: { aem_id?: string; locale?: string; status?: string } = {};
            obj['aem_id'] = pl.aem_id;
            obj['locale'] = pl.locale;
            obj['status'] = pl.status;
            arr.push(obj as { aem_id: string; locale: string; status: string });
        });
        return arr;
    };

    deletePlaylists = async (selectedPlaylists) => {
        const data = this.formatDeletePL(selectedPlaylists);
        try {
            const url = `${BASE_API_URL}/playlist`;
            const headers = this.getBaseHeaders();
            const response = await request(url, headers, BACKEND_ERROR_MESSAGE, {}, 'delete', data, 20000);
            return response.data;
        } catch (error: unknown) {
            // TODO: ADD ERROR LOGGING
            console.error('error ====>', error);
            return { data: error, failed: true };
        }
        //return { failed: false, data: { data: selectedPlaylists } };
    };

    deleteCollections = async (selectedCollections) => {
        const data = this.formatDeletePL(selectedCollections);
        try {
            const url = `${BASE_API_URL}/collection`;
            const headers = this.getBaseHeaders();
            const response = await request(url, headers, BACKEND_ERROR_MESSAGE, {}, 'delete', data, 20000);
            return response.data;
        } catch (error: unknown) {
            // TODO: ADD ERROR LOGGING
            console.error('error ====>', error);
            return { data: error, failed: true };
        }
        //return { failed: false, data: { data: selectedPlaylists } };
    };

    fileUpload = async (data) => {
        try {
            const url = `${BASE_API_URL}/playlist/upload`;
            const headers = this.getBaseHeaders();
            headers['Content-Type'] = 'multipart/form-data';
            const response = await request(url, headers, BACKEND_ERROR_MESSAGE, {}, 'PUT', data, 60000);
            if (response?.data?.data?.failed === true) return response?.data?.data;
            else {
                return response?.data;
            }
        } catch (error: unknown) {
            // TODO: ADD ERROR LOGGING
            console.error('error ====>', error);
            return { data: error, failed: true };
        }
        //return { failed: false, data: { data: selectedPlaylists } };
    };

    TBWUploadAsserts = async (data, docId, appName) => {
        try {
            const url = `${BASE_INAPP_API_URL}/v2/tutorial/${docId}/upload?appName=${appName}`;
            const headers = this.getBaseHeaders();
            headers['Content-Type'] = 'multipart/form-data';
            const response = await request(url, headers, BACKEND_ERROR_MESSAGE, {}, 'PUT', data, 60000);
            return response;
        } catch (error: unknown) {
            // TODO: ADD ERROR LOGGING
            console.error('TBWUploadAsserts > error ====>', error);
            return { data: error, failed: true };
        }
        //return { failed: false, data: { data: selectedPlaylists } };
    };

    generateSuggestions = async (data, path: 'all' | 'section' | undefined, appName: string, locale: string) => {
        try {
            const url = `${BASE_INAPP_API_URL}/v1/content/tutorial-inapp/suggestions/${path ?? 'all'}?appName=${appName}&locale=${locale}`;
            const headers = this.getBaseHeaders();
            const response = await request(url, headers, BACKEND_ERROR_MESSAGE, {}, 'POST', data, 60000);
            return response;
        } catch (error: unknown) {
            // TODO: ADD ERROR LOGGING
            console.error('error fetching suggestions', error);
            return { data: error, failed: true };
        }
    };

    fetchTags = async (appName, locale) => {
        try {
            const url = `${BASE_INAPP_API_URL}/v2/tags?appName=${appName}&locale=${locale}`;
            const headers = this.getBaseHeaders();
            const response = await request(url, headers, BACKEND_ERROR_MESSAGE, {}, 'get', '', 20000);
            return response?.data?.data?.tags;
        } catch (error: unknown) {
            // TODO: ADD ERROR LOGGING
            console.error('fetchTags > error ====>', error);
            return { data: error, failed: true };
        }
    };

    fetchDashboardData = async (locale, appName) => {
        try {
            const url = `${BASE_INAPP_API_URL}/v2/tutorial/metadata?appName=${appName}&locale=${locale}&additionalInfo=true`;
            const headers = this.getBaseHeaders();
            const response = await request(url, headers, BACKEND_ERROR_MESSAGE, {}, 'get', '', 60000);
            return response?.data;
        } catch (error: unknown) {
            // TODO: ADD ERROR LOGGING
            console.error('fetchDashboardData > error ====>', error);
            return { data: error, failed: true };
        }
        //return { failed: false, data: { data: selectedPlaylists } };
    };

    deleteUtut = async (docId, appName, locale) => {
        try {
            const url = `${BASE_INAPP_API_URL}/v2/tutorial/${docId}/delete?appName=${appName}&locale=${locale}`;
            const headers = this.getBaseHeaders();
            const response = await request(url, headers, BACKEND_ERROR_MESSAGE, {}, 'delete', {}, 20000);
            return response;
        } catch (error: unknown) {
            // TODO: ADD ERROR LOGGING
            console.error('deleteUtut > error ====>', error);
            return { data: error, failed: true };
        }
        //return { failed: false, data: { data: selectedPlaylists } };
    };

    duplicateCheck = async (docId, appName) => {
        try {
            const url = `${BASE_INAPP_API_URL}/v2/tutorial/${docId}/check?appName=${appName}`;
            const headers = this.getBaseHeaders();
            const response = await request(url, headers, BACKEND_ERROR_MESSAGE, {}, 'get', '', 20000);
            if (response?.failed) {
                return true;
            } else {
                if (response?.data['EXISTS']) {
                    return true;
                }
                return false;
            }
        } catch (error: unknown) {
            // TODO: ADD ERROR LOGGING
            console.error('duplicateCheck > error ====>', error);
            return true;
        }
        //return { failed: false, data: { data: selectedPlaylists } };
    };

    transientData = (docId: string) => {
        const data: Record<string, string> = {};
        data['operation'] = 'replaceDocId';
        data['newDocId'] = docId;
        return data;
    };

    approveWithTransientId = async (docId: string, newDocId: string, locale: string, appName: string) => {
        try {
            const url = `${BASE_INAPP_API_URL}/v2/tutorial/${docId}/approve?appName=${appName}&locale=${locale}`;
            const data = this.transientData(newDocId);
            const headers = this.getBaseHeaders();
            const response = await request(url, headers, BACKEND_ERROR_MESSAGE, {}, 'POST', data, 20000);
            return response;
        } catch (error: unknown) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            // TODO: ADD ERROR LOGGING
            console.error(`Approve with transient failed > ${errorMessage}`);
            return { data: error, failed: true };
        }
    };

    movetoReview = async (docId, locale, appName) => {
        try {
            const url = `${BASE_INAPP_API_URL}/v2/tutorial/${docId}/movetoreview?appName=${appName}&locale=${locale}`;
            const headers = this.getBaseHeaders();
            const response = await request(url, headers, BACKEND_ERROR_MESSAGE, {}, 'POST', {}, 20000);
            return response;
        } catch (error: unknown) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            // TODO: ADD ERROR LOGGING
            console.error(`movetoReview failed > ${errorMessage}`);
            return { data: error, failed: true };
        }
    };

    createNewVersion = async (docId, locale, appName) => {
        try {
            const url = `${BASE_INAPP_API_URL}/v2/tutorial/${docId}/createnewversion?appName=${appName}&locale=${locale}`;
            const headers = this.getBaseHeaders();
            const response = await request(url, headers, BACKEND_ERROR_MESSAGE, {}, 'PUT', {}, 20000);
            return response;
        } catch (error: unknown) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            // TODO: ADD ERROR LOGGING
            console.error(`createNewVersion failed > ${errorMessage}`);
            return { data: error, failed: true };
        }
    };

    fetchPlaylistTags = async (product, locale = 'en_US', version = 3) => {
        try {
            const url = `${BASE_API_URL}/playlist/tags`;
            const headers = this.getBaseHeaders();
            const params = {
                product,
                version,
                locale,
            };

            const response = await request(url, headers, BACKEND_ERROR_MESSAGE, params, 'get', '', 20000);

            return response;
        } catch (error: unknown) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            // TODO: ADD ERROR LOGGING
            console.error(`${BACKEND_ERROR_MESSAGE} OxfordService > fetchAllTags > ${errorMessage}`);
            return { data: error, failed: true };
        }
    };
}

export default LCSServices;
